import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Form } from '@hologram-dimension/form';
import { FormField } from '@hologram-dimension/form-field';
import { RadioField } from '@hologram-dimension/radio-field';
import { Select } from '@hologram-dimension/select';
import { TextInput } from '@hologram-dimension/text-input';
import useSetUsageAlerts from 'api/legacy/devices/useSetUsageAlerts';
import clsx from 'clsx/lite';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import type { AlertToggle, HologramSimIdsMap, OnSubmit } from 'types/common';
import formatNumber from 'utils/numbers/formatNumber';
import styles from './SetUsageAlerts.module.css';

interface SetUsageAlertsProps {
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims: Array<HologramSimId>) => void;
  onAlertingToggle: (alerting: AlertToggle) => void;
}

interface UsageAlertForm {
  usageAlertThresholdBytes?: number;
  usageAlertThresholdUnit?: number;
  usageAlertThreshold: AlertToggle;
}

const unitToBytes = {
  KB: 1_000,
  MB: 1_000_000,
  GB: 1_000_000_000,
};

const unitOptions = Object.entries(unitToBytes).map(([option, value]) => ({
  value: value.toString(),
  label: option,
}));

const SetUsageAlerts = forwardRef<OnSubmit, SetUsageAlertsProps>(
  ({ sims, onProcessing, onDone, onAlertingToggle }, ref) => {
    const {
      register,
      handleSubmit,
      watch,
      setFocus,
      formState: { errors },
    } = useForm<UsageAlertForm>({
      mode: 'onSubmit',
      defaultValues: { usageAlertThresholdUnit: unitToBytes.MB, usageAlertThreshold: 'noalert' },
    });

    const alerting = watch('usageAlertThreshold');
    const adding = alerting === 'alert';
    const newAlertThresholdBytes = watch('usageAlertThresholdBytes');
    const newAlertThresholdUnit = watch('usageAlertThresholdUnit');
    const newAlertThreshold = (newAlertThresholdBytes ?? 0) * (newAlertThresholdUnit ?? 0);

    useEffect(() => {
      onAlertingToggle(alerting);
    }, [alerting, onAlertingToggle]);

    useEffect(() => {
      if (adding) {
        setFocus('usageAlertThresholdBytes');
      }
    }, [adding, setFocus]);

    const deviceIds = useMemo(
      () => [...sims.values()].filter((deviceId) => deviceId !== undefined),
      [sims]
    );
    const setUsageAlertsApi = useSetUsageAlerts(deviceIds);

    const [apiError, setApiError] = useState<string>();

    const commitStateChange = useCallback<SubmitHandler<UsageAlertForm>>(
      async (data) => {
        onProcessing(true);

        // We let form validation prevent us from getting null/undefined values.
        const thresholdApiValue = adding
          ? (data.usageAlertThresholdBytes ?? 0) * (data.usageAlertThresholdUnit ?? 0)
          : -1;

        try {
          await setUsageAlertsApi(thresholdApiValue);
        } catch (ex) {
          if (typeof ex === 'string') {
            setApiError(ex);
          } else {
            setApiError(`An error occurred: ${ex}`);
          }
        } finally {
          onProcessing(false);
        }

        // This is a batch job, so we don't have a way to know if the individual jobs succeeded or not. Assume all invalid.
        onDone([...sims.keys()]);
      },
      [adding, onDone, onProcessing, setUsageAlertsApi, sims]
    );

    const onSubmit = handleSubmit(commitStateChange);

    const formRef = useRef<HTMLFormElement>(null);

    const manualSubmit = useCallback(async () => {
      formRef.current?.requestSubmit();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: manualSubmit,
      }),
      [manualSubmit]
    );
    const content = useMemo(() => {
      if (apiError) {
        return <BannerNotification variant="system-error">{apiError}</BannerNotification>;
      }

      return (
        <Form onSubmit={onSubmit} ref={formRef} className={styles.form}>
          <FormField
            className={clsx(styles.formField, styles.alertLabel)}
            label="Alert behavior"
            layoutDirection="row"
            centerLabel
          >
            {() => (
              <span>
                {!adding || newAlertThreshold === 0 || Number.isNaN(newAlertThreshold) ? (
                  <>
                    <strong>Do not check data usage</strong> for{' '}
                    {sims.size === 1 ? 'this SIM' : 'these SIMs'}.
                  </>
                ) : (
                  <>
                    {sims.size === 1 ? 'This SIM' : 'These SIMs'} will{' '}
                    <strong>
                      be alerted at {formatNumber(newAlertThresholdBytes!)}{' '}
                      {
                        Object.entries(unitToBytes).find(
                          ([, bytes]) => bytes === newAlertThresholdUnit
                        )![0]
                      }
                    </strong>{' '}
                    of data usage.
                  </>
                )}
              </span>
            )}
          </FormField>
          <RadioField
            className={clsx(styles.formField, styles.alertToggle)}
            label="Usage alert threshold"
            layoutDirection="row"
            options={[
              { label: 'Do not alert', value: 'noalert' },
              { label: 'Alert at a set threshold', value: 'alert' },
            ]}
            {...register('usageAlertThreshold')}
          />
          <FormField
            className={clsx(styles.formField, styles.thresholdEntryField)}
            label="Usage alert threshold"
            hiddenLabel
            layoutDirection="row"
            invalid={!!errors.usageAlertThresholdBytes}
            validationMessage={errors.usageAlertThresholdBytes?.message}
          >
            {({ primaryControlId, ariaDescribedbyIds, invalid }) => (
              <div className={styles.thresholdEntry}>
                <div className={styles.thresholdEntryControls}>
                  {adding && (
                    <>
                      <TextInput
                        id={primaryControlId}
                        ariaDescribedby={ariaDescribedbyIds}
                        ariaLabel="Usage alert threshold bytes"
                        invalid={invalid}
                        className={styles.byteEntry}
                        type="number"
                        {...register('usageAlertThresholdBytes', {
                          valueAsNumber: true,
                          validate: (value) => {
                            if (value === undefined || value < 0) {
                              return 'Please enter a valid amount greater than or equal to 0.';
                            }
                            if (value !== Math.floor(value)) {
                              return 'Please enter a valid whole number greater than or equal to 0.';
                            }

                            return true;
                          },
                        })}
                      />
                      <Select
                        id="customAmountUnit"
                        className={styles.unitEntry}
                        ariaDescribedby={ariaDescribedbyIds}
                        ariaLabel="Usage alert threshold unit"
                        options={unitOptions}
                        {...register('usageAlertThresholdUnit', {
                          valueAsNumber: true,
                        })}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </FormField>
        </Form>
      );
    }, [
      adding,
      apiError,
      errors.usageAlertThresholdBytes,
      newAlertThreshold,
      newAlertThresholdBytes,
      newAlertThresholdUnit,
      onSubmit,
      register,
      sims.size,
    ]);

    return (
      <div className={styles.wrapper}>
        <div className={styles.forms}>
          <BannerNotification variant="caution">
            Data usage alerts do not send notifications and are only found in the Action Items tab
            under the Home page.
          </BannerNotification>
          {content}
        </div>
      </div>
    );
  }
);

export default SetUsageAlerts;
